import { useCallback, useEffect, useRef, useState } from 'react';
import { markTourSeen, pendingTours } from '../tutorial/progress';
import { Tour, TutorialStep, targetSelector } from '../tutorial/tours';

/** How often the spotlight re-measures its target (scroll, resize, relayout). */
const MEASURE_INTERVAL_MS = 150;
/**
 * How long a step waits for a usable target before giving up on it. A
 * spotlight on nothing — or on a control the operator cannot press — would
 * trap them behind a dimmed screen, so the step is skipped rather than
 * blocking the UI.
 */
const MISSING_TARGET_TIMEOUT_MS = 1500;

/** A disabled control can never be clicked, so it can never advance a step. */
const isUnusable = (el: Element): boolean =>
  el.matches(':disabled, [aria-disabled="true"]');

/** Viewport-relative box of the spotlit element. */
export interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface TutorialState {
  tour: Tour | null;
  step: TutorialStep | null;
  /** 1-based position of the current step, for the "2 of 5" counter. */
  position: number;
  total: number;
  rect: SpotlightRect | null;
  /** The spotlit element, so the caller can focus it. */
  target: Element | null;
  /** Dismiss the tour — it is recorded as seen and will not run again. */
  skip: () => void;
}

const sameRect = (a: SpotlightRect, b: DOMRect): boolean =>
  a.top === b.top &&
  a.left === b.left &&
  a.width === b.width &&
  a.height === b.height;

/**
 * Runs the guided tutorial for one operator (issue #427).
 *
 * Starts the oldest tour they have not seen — one per sign-in, so finishing a
 * tour never rolls straight into another — and advances when they actually
 * click the spotlit element. Completing or skipping records the tour as seen.
 * `userKey` is the OIDC `sub`; null (not signed in yet) runs nothing.
 */
export const useTutorial = (userKey: string | null): TutorialState => {
  const [tour, setTour] = useState<Tour | null>(null);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const [target, setTarget] = useState<Element | null>(null);
  // Read inside listeners, which must not be re-bound on every measurement.
  const targetRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!userKey) {
      return;
    }
    const [next] = pendingTours(userKey);
    setTour(next ?? null);
    setIndex(0);
  }, [userKey]);

  const step = tour ? (tour.steps[index] ?? null) : null;

  const clearTarget = useCallback(() => {
    targetRef.current = null;
    setTarget(null);
    setRect(null);
  }, []);

  const end = useCallback(() => {
    if (userKey && tour) {
      markTourSeen(userKey, tour.id);
    }
    setTour(null);
    setIndex(0);
    clearTarget();
  }, [clearTarget, tour, userKey]);

  const advance = useCallback(() => {
    if (!tour) {
      return;
    }
    if (index + 1 < tour.steps.length) {
      setIndex(index + 1);
      clearTarget();
      return;
    }
    end();
  }, [clearTarget, end, index, tour]);

  // Track the target's position for as long as the step is live.
  useEffect(() => {
    if (!step) {
      return;
    }
    const deadline = Date.now() + MISSING_TARGET_TIMEOUT_MS;
    const measure = () => {
      const el = document.querySelector(targetSelector(step.target));
      if (!el || isUnusable(el)) {
        if (Date.now() > deadline) {
          advance();
        }
        return;
      }
      targetRef.current = el;
      setTarget((prev) => (prev === el ? prev : el));
      const box = el.getBoundingClientRect();
      setRect((prev) =>
        prev && sameRect(prev, box)
          ? prev
          : { top: box.top, left: box.left, width: box.width, height: box.height }
      );
    };
    measure();
    const timer = window.setInterval(measure, MEASURE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [advance, step]);

  // Advance on the real action: a click that lands on the spotlit element.
  // Capture phase, and never prevented — the app's own handler still runs.
  useEffect(() => {
    if (!step) {
      return;
    }
    const onClick = (e: MouseEvent) => {
      const el = targetRef.current;
      if (el && e.target instanceof Node && el.contains(e.target)) {
        advance();
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [advance, step]);

  // Escape dismisses the tutorial, and only the tutorial: it is swallowed so
  // it never also cancels whatever mode the operator is in underneath.
  useEffect(() => {
    if (!step) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        end();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [end, step]);

  return {
    tour,
    step,
    position: index + 1,
    total: tour ? tour.steps.length : 0,
    rect,
    target,
    skip: end,
  };
};
